// SB6-ANA-PROG-02 — per-widget filter-sync indicator (Q9: header spinner).
//
// A ~14px spinner shown in a widget's header while the widget is
// out-of-sync with the latest Global Data Point Filter revision (a refetch
// is in flight or queued). When in sync the component renders nothing
// (zero layout shift is achieved by the consumers reserving the slot — see
// `WIDGET_SYNC_INDICATOR_SIZE` and the per-widget header layouts).
//
// A11y: the spinner itself is `aria-hidden` (decorative motion); the text
// alternative is an `aria-live="polite"` visually-hidden node so a screen
// reader announces "Applying filter…" → "Filter applied" as the widget
// settles, without a visual layout change. Follows the
// `motion-and-loading-states` skill (subtle, non-blocking, announces state
// transitions politely).
import { Box, CircularProgress } from "@mui/material";

/** Fixed indicator footprint — consumers reserve this to avoid CLS. */
export const WIDGET_SYNC_INDICATOR_SIZE = 14;

export interface WidgetSyncIndicatorProps {
  /** True while the widget is out-of-sync (spinner shown). */
  outOfSync: boolean;
  /**
   * Accessible widget name woven into the live-region text, e.g.
   * "AI Filter Module" → "Applying filter to AI Filter Module…".
   */
  label: string;
  /** Optional `data-testid` (defaults to `widget-sync-indicator`). */
  testId?: string;
}

/**
 * Render the sync spinner + its polite text alternative. The outer Box
 * always occupies the same fixed square so toggling `outOfSync` never
 * reflows the header (CLS 0).
 */
export function WidgetSyncIndicator({
  outOfSync,
  label,
  testId = "widget-sync-indicator",
}: WidgetSyncIndicatorProps): React.ReactElement {
  return (
    <Box
      data-testid={testId}
      data-out-of-sync={outOfSync ? "true" : "false"}
      sx={{
        // Reserve the footprint at all times so the header never shifts
        // when the spinner appears/disappears (AC6 — zero layout shift).
        width: WIDGET_SYNC_INDICATOR_SIZE,
        height: WIDGET_SYNC_INDICATOR_SIZE,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {outOfSync ? (
        <CircularProgress
          size={WIDGET_SYNC_INDICATOR_SIZE}
          thickness={5}
          aria-hidden="true"
          // Brand-blue, subtle — matches motion-and-loading-states.
          sx={{ color: "primary.main" }}
        />
      ) : null}
      {/* Visually-hidden polite live region — the text alternative. */}
      <Box
        component="span"
        role="status"
        aria-live="polite"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          p: 0,
          m: -0.125,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {outOfSync ? `Applying filter to ${label}…` : `${label} filter applied`}
      </Box>
    </Box>
  );
}

/**
 * Opacity applied to a widget's counts/rows while it is stale. Chosen so
 * dimmed text still clears WCAG AA contrast against the widget surface
 * (0.6 keeps `text.primary` / `text.secondary` above the 4.5:1 / 3:1
 * thresholds in both light and dark themes — verified against the token
 * palette; do not lower without re-auditing per `wcag22-aa-audit`).
 */
export const WIDGET_STALE_OPACITY = 0.6;
